import { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

// ============================================================================
// Tabs — the canonical tab strip. REDESIGNED 2026-06-10 (DESIGN-SPRINT 2d,
// Seth: "make the tabs look better and operate more like tabs"): a segmented
// control — the tab row is a soft recessed track, the ACTIVE tab is a raised
// white pill that GLIDES between positions (per-instance layoutId so multiple
// strips never cross-animate; honors prefers-reduced-motion). Clicking feels
// physical: the pill moves to your finger. One source for Customer 360, the
// Agreements command center, and the Board.
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

export interface TabItem {
  key: string;
  label: string;
  /** NAV-360-01: leading icon for the "bar" variant (icon + label, never icon-only). */
  icon?: LucideIcon;
  /** Optional trailing count, rendered as a small badge. Pass null/"" to omit. */
  count?: number | string | null;
  /** TODAY-REDESIGN-02: "amber" marks a count that demands a human today —
   *  the one color the calm scoreboard allows. Only the bar variant uses it;
   *  the active tab keeps brand (the ink bar already says "you are here"). */
  countTone?: "neutral" | "amber";
  /** Muted "soon" marker for not-yet-built destinations. */
  soon?: boolean;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  /** "pill" (default) = the segmented control. "panel" (TABS-360, Seth
   *  2026-08-21: "stretch the length… look more connected") = full-width
   *  browser-style tabs: a baseline runs the whole width and the ACTIVE tab
   *  sits ON it — white, bordered, cut into the line — so the tab visibly
   *  belongs to the content underneath. */
  variant?: "pill" | "panel" | "bar";
  /** "sm" (360-B): tighter padding + 13px labels so eleven tabs fit beside
   *  the profile rail instead of scrolling off the right edge. */
  size?: "md" | "sm";
}

export function Tabs({ items, value, onChange, className, variant = "pill", size = "md" }: TabsProps) {
  const instanceId = useId();
  const reduce = useReducedMotion();

  // NAV-360-01 (Seth approved the mock 2026-10-09): the modern tab BAR —
  // icon+label tabs with a sliding brand ink-bar under the active one.
  // Same gliding layoutId trick as the pill; counts answer "is anything
  // there?" before the click. The 360 header is its first home.
  // NAV-360-04 (Seth 2026-10-12: "maybe the tabs look a little bit more
  // like tabs"): 48px tall, and the ACTIVE tab is a filled rounded-top
  // shape — a tangible tab object sitting on the baseline, not floating
  // text with a line under it.
  if (variant === "bar") {
    return (
      <div
        role="tablist"
        className={cn(
          "flex w-full items-stretch gap-0.5 overflow-x-auto overflow-y-hidden border-b border-hairline [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        {items.map((t) => {
          const active = t.key === value;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(t.key)}
              className={cn(
                // NAV-360-05 (Seth: "use the whole nav row — 'Notes' is
                // being cut off on smaller screens"): every tab grows to
                // share the full row (flex-1, centered); min-w-fit keeps
                // labels whole, and only truly narrow screens scroll.
                // HDR-DISPATCH-01: inside a @container narrower than 1100px
                // (the 360 band needs ~1096px for nine iconed tabs with
                // counts; a 1180 laptop with the rail open gives it ~800)
                // the tabs shed their icons and tighten, so all nine still
                // fit instead of scrolling off behind "Comms".
                "relative flex min-h-[48px] min-w-fit flex-1 items-center justify-center gap-1.5 rounded-t-xl font-semibold whitespace-nowrap transition-colors duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                size === "sm" ? "px-3 text-[13.5px] @max-[1100px]:px-2 @max-[1100px]:text-[13px]" : "px-4 text-sm",
                active
                  ? "bg-brand-50/70 text-brand-700"
                  : "text-slate-500 hover:bg-slate-900/[0.04] hover:text-slate-800",
              )}
            >
              {Icon && <Icon className={cn("h-4 w-4 shrink-0 @max-[1100px]:hidden", active ? "text-brand-600" : "text-slate-400")} strokeWidth={1.9} />}
              {t.label}
              {t.count != null && t.count !== "" && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-px text-[10.5px] font-bold tabular-nums",
                    active
                      ? "bg-brand-50 text-brand-700"
                      : t.countTone === "amber"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-200/70 text-slate-500",
                  )}
                >
                  {t.count}
                </span>
              )}
              {t.soon && (
                <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  soon
                </span>
              )}
              {/* The sliding ink bar — "you are here", physically. */}
              {active && (
                <motion.span
                  layoutId={`tabs-ink-${instanceId}`}
                  className="absolute inset-x-1.5 -bottom-px h-[3px] rounded-t-full bg-brand-600"
                  transition={reduce ? { duration: 0 } : { duration: 0.22, ease: EASE_OUT_EXPO }}
                />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === "panel") {
    return (
      <div
        role="tablist"
        // overflow-y-hidden + hidden scrollbar (Seth 2026-09-01): the 1px
        // hover-lift used to overflow vertically, and Windows Chrome drew a
        // micro ▲●▼ scrollbar in the tab strip. Wheel/touch still scroll
        // horizontally when the window is narrow.
        className={cn("flex w-full items-end overflow-x-auto overflow-y-hidden pt-1 border-b border-hairline [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className)}
      >
        {items.map((t) => {
          const active = t.key === value;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(t.key)}
              className={cn(
                "group relative -mb-px min-w-fit flex-1 whitespace-nowrap rounded-t-xl border font-semibold transition-all duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                size === "sm" ? "px-2.5 py-2 text-[13px]" : "px-4 py-2.5 text-sm",
                active
                  ? "border-hairline border-b-white bg-white text-brand-700"
                  : "border-transparent border-b-hairline text-slate-500 hover:-translate-y-px hover:text-slate-800 motion-reduce:hover:translate-y-0",
              )}
            >
              <span className="relative mx-auto flex w-fit items-center gap-1.5">
                {t.label}
                {t.count != null && t.count !== "" && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums",
                      active ? "bg-brand-50 text-brand-700" : "bg-slate-200/70 text-slate-500",
                    )}
                  >
                    {t.count}
                  </span>
                )}
                {t.soon && (
                  <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    soon
                  </span>
                )}
                {/* Hover hint: a quiet underline grows in — the tab answers the cursor. */}
                {!active && (
                  <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 rounded-full bg-brand-300 transition-all duration-200 group-hover:w-full" />
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-slate-200/50 p-1",
        className,
      )}
    >
      {items.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={cn(
              "relative shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
              active
                ? "text-slate-900"
                : "text-slate-500 hover:bg-white/60 hover:text-slate-800",
            )}
          >
            {/* The raised pill glides under the active label. */}
            {active && (
              <motion.span
                layoutId={`tabs-pill-${instanceId}`}
                className="absolute inset-0 rounded-full bg-white shadow-e1 ring-1 ring-black/[0.04]"
                transition={
                  reduce ? { duration: 0 } : { duration: 0.25, ease: EASE_OUT_EXPO }
                }
              />
            )}
            <span className="relative flex items-center gap-1.5">
              {t.label}
              {t.count != null && t.count !== "" && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums",
                    active ? "bg-brand-50 text-brand-700" : "bg-slate-200/70 text-slate-500",
                  )}
                >
                  {t.count}
                </span>
              )}
              {t.soon && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  soon
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
